import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, IconRefresh } from '@screentone/core';

import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { PageDto, SectionContainer } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import useIsNewSectionPage from 'hooks/useIsNewSectionPage';
import { getLayoutIdFromPageRoot } from 'utils/layoutUtils';
import { hasMultiZoneModules } from 'utils/pageUtils';
import { CloneLayoutModal } from './CloneLayoutModal';

interface CloneLayoutProps {
  isReadOnly?: boolean;
}

export const CloneLayout: FC<CloneLayoutProps> = ({ isReadOnly = false }) => {
  const isNewSectionPage = useIsNewSectionPage();
  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  const currentProperty = useConvertedProperty();
  const { getDTO } = useDataModelContext<SectionContainer>();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  // Don't show if creating a new page
  if (isNewSectionPage) {
    return null;
  }

  // Only show for admins/supers on WSJ property
  if (!(isAdmin || isSuper) || currentProperty !== 'wsj') {
    return null;
  }

  const pageDTO = getDTO() as PageDto;
  const { allessehId } = pageDTO.metadata;
  const pageTitle = pageDTO.metadata.name || '';

  const layoutId = getLayoutIdFromPageRoot(pageDTO.root) ?? 'baseline';

  // Don't show for pages with multi-zone modules
  if (hasMultiZoneModules(pageDTO.root)) {
    return null;
  }

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCloneLayout = (pageTitle: string) => {
    const params = new URLSearchParams({
      allessehId,
      layoutId,
      ...(pageTitle && { pageTitle })
    });
    const url = `/wsj/pages/new?${params.toString()}`;
    navigate(url);
  };

  return (
    <>
      <Button
        tertiary
        icon={IconRefresh}
        onClick={handleOpenModal}
        data-testid="clone-layout-button"
        disabled={isReadOnly}
      >
        Clone Layout
      </Button>
      {showModal && (
        <CloneLayoutModal
          pageTitle={pageTitle}
          onDismiss={handleCloseModal}
          onConfirm={handleCloneLayout}
          isReadOnly={isReadOnly}
        />
      )}
    </>
  );
};
